import React from "react";

import {
    Alert,
    Badge,
    Button,
    Group,
    Modal,
    Paper,
    Stack,
    Table,
    Text,
} from "@mantine/core";

import {
    IconAlertCircle,
    IconCreditCard,
    IconEdit,
    IconUsers,
} from "@tabler/icons-react";

import { HotelBookingDto } from "../../api/hotelBookingApi";

import {
    formatDate,
    formatDateTime,
    formatMoney,
    getBookingStatusDescription,
    getRoomingProgress,
    getStatusColor,
    getStatusText,
    isRoomingDeadlinePassed,
    normalizeStatus,
} from "../../utils/hotelBookingUtils";

import {
    BookingAction,
    HotelBookingTexts,
} from "./HotelBookingTypes";

type Props = {
    opened: boolean;
    booking: HotelBookingDto | null;
    onClose: () => void;
    showEdit?: boolean;

    t: HotelBookingTexts;
    language: "en" | "da";

    onEdit?: BookingAction;
    onPay: BookingAction;
    onRooming: BookingAction;
};

export default function BookingDetailsModal({
    opened,
    booking,
    onClose,
    showEdit = true,
    t,
    language,
    onEdit,
    onPay,
    onRooming,
}: Props) {
    const progress = booking
        ? getRoomingProgress(booking)
        : null;

    const deadlinePassed = booking
        ? isRoomingDeadlinePassed(
            booking
        )
        : false;

    const status = booking
        ? normalizeStatus(
            booking.status
        )
        : "";

    const canEdit =
        !!booking &&
        !booking.isPaid &&
        !booking.paidAt &&
        (
            status ===
            "pendingapproval" ||
            status ===
            "awaitingpayment"
        );

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                booking
                    ? `${t.booking} #${booking.hotelBookingID}`
                    : t.booking
            }
            size="xl"
        >
            {booking && (
                <Stack gap="md">
                    <Group justify="space-between">
                        <Stack gap={2}>
                            <Text fw={800}>
                                {
                                    booking.hotelName
                                }
                            </Text>

                            <Text
                                size="sm"
                                c="dimmed"
                            >
                                {
                                    booking.country
                                }
                            </Text>
                        </Stack>

                        <Badge
                            color={getStatusColor(
                                booking.status
                            )}
                        >
                            {getStatusText(
                                booking.status,
                                language
                            )}
                        </Badge>
                    </Group>

                    <Alert
                        color={getStatusColor(
                            booking.status
                        )}
                        variant="light"
                    >
                        {getBookingStatusDescription(
                            booking.status,
                            language
                        )}
                    </Alert>

                    <Group grow>
                        <Paper
                            withBorder
                            p="md"
                            radius="md"
                        >
                            <Text
                                size="xs"
                                c="dimmed"
                            >
                                {t.total}
                            </Text>

                            <Text fw={900}>
                                {formatMoney(
                                    booking.totalPrice
                                )}
                            </Text>
                        </Paper>

                        <Paper
                            withBorder
                            p="md"
                            radius="md"
                        >
                            <Text
                                size="xs"
                                c="dimmed"
                            >
                                {t.rooms}
                            </Text>

                            <Text fw={900}>
                                {booking.rooms
                                    ?.length ?? 0}
                            </Text>
                        </Paper>

                        <Paper
                            withBorder
                            p="md"
                            radius="md"
                        >
                            <Text
                                size="xs"
                                c="dimmed"
                            >
                                {t.created}
                            </Text>

                            <Text fw={900}>
                                {formatDateTime(
                                    booking.createdAt
                                )}
                            </Text>
                        </Paper>
                    </Group>

                    {canEdit &&
                        status ===
                        "awaitingpayment" && (
                            <Alert
                                color="yellow"
                                variant="light"
                                icon={
                                    <IconAlertCircle
                                        size={18}
                                    />
                                }
                            >
                                {language ===
                                    "en"
                                    ? "If you change the booking, the existing payment link will be replaced with a new one."
                                    : "Hvis du retter bookingen, bliver det eksisterende betalingslink erstattet med et nyt."}
                            </Alert>
                        )}

                    {progress && (
                        <Alert
                            color={
                                progress.missingGuests >
                                    0
                                    ? "yellow"
                                    : "green"
                            }
                            variant="light"
                            icon={
                                <IconUsers
                                    size={18}
                                />
                            }
                        >
                            {t.roomingProgress}
                            :{" "}
                            <strong>
                                {
                                    progress.filledGuests
                                }
                                /
                                {
                                    progress.totalGuests
                                }{" "}
                                {t.filled}
                            </strong>
                            {" · "}
                            <strong>
                                {
                                    progress.missingGuests
                                }{" "}
                                {
                                    t.missingNames
                                }
                            </strong>
                        </Alert>
                    )}

                    {booking.roomingDeadline && (
                        <Alert
                            color={
                                deadlinePassed
                                    ? "yellow"
                                    : "blue"
                            }
                            variant="light"
                            icon={
                                <IconAlertCircle
                                    size={18}
                                />
                            }
                        >
                            {t.roomingDeadline}:{" "}
                            {formatDateTime(
                                booking.roomingDeadline
                            )}

                            {deadlinePassed
                                ? ` · ${t.deadlinePassed}`
                                : ""}
                        </Alert>
                    )}

                    <Table
                        withTableBorder
                        striped
                    >
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>
                                    #
                                </Table.Th>
                                <Table.Th>
                                    {
                                        t.roomType
                                    }
                                </Table.Th>
                                <Table.Th>
                                    {t.checkIn}
                                </Table.Th>
                                <Table.Th>
                                    {t.checkOut}
                                </Table.Th>
                                <Table.Th>
                                    {t.guests}
                                </Table.Th>
                                <Table.Th>
                                    {t.total}
                                </Table.Th>
                            </Table.Tr>
                        </Table.Thead>

                        <Table.Tbody>
                            {booking.rooms?.map(
                                (
                                    room,
                                    index
                                ) => (
                                    <Table.Tr
                                        key={
                                            room.hotelBookingRoomID
                                        }
                                    >
                                        <Table.Td>
                                            {index +
                                                1}
                                        </Table.Td>

                                        <Table.Td>
                                            {
                                                room.roomTypeName
                                            }
                                        </Table.Td>

                                        <Table.Td>
                                            {formatDate(
                                                room.checkInDate
                                            )}
                                        </Table.Td>

                                        <Table.Td>
                                            {formatDate(
                                                room.checkOutDate
                                            )}
                                        </Table.Td>

                                        <Table.Td>
                                            {
                                                room.guestsCount
                                            }
                                        </Table.Td>

                                        <Table.Td>
                                            {formatMoney(
                                                room.totalPrice
                                            )}
                                        </Table.Td>
                                    </Table.Tr>
                                )
                            )}
                        </Table.Tbody>
                    </Table>

                    <Group justify="flex-end">
                        {showEdit && canEdit && onEdit && (
                            <Button
                                color="orange"
                                variant="light"
                                leftSection={
                                    <IconEdit
                                        size={16}
                                    />
                                }
                                onClick={() => {
                                    onClose();
                                    onEdit?.(booking);
                                }}
                            >
                                {language ===
                                    "en"
                                    ? "Edit booking"
                                    : "Ret booking"}
                            </Button>
                        )}

                        {status ===
                            "awaitingpayment" &&
                            booking.paymentLink && (
                                <Button
                                    color="green"
                                    leftSection={
                                        <IconCreditCard
                                            size={16}
                                        />
                                    }
                                    onClick={() =>
                                        onPay(
                                            booking
                                        )
                                    }
                                >
                                    {t.payNow}
                                </Button>
                            )}

                        {status ===
                            "paid" && (
                                <Button
                                    color="violet"
                                    leftSection={
                                        <IconUsers
                                            size={16}
                                        />
                                    }
                                    onClick={() =>
                                        onRooming(
                                            booking
                                        )
                                    }
                                >
                                    {t.fillGuests}
                                </Button>
                            )}

                        {status ===
                            "roomassignmentopen" && (
                                <Button
                                    color="violet"
                                    leftSection={
                                        <IconUsers
                                            size={16}
                                        />
                                    }
                                    onClick={() =>
                                        onRooming(
                                            booking
                                        )
                                    }
                                >
                                    {
                                        t.continueRooming
                                    }
                                </Button>
                            )}
                    </Group>
                </Stack>
            )}
        </Modal>
    );
}